import { createRoot } from 'react-dom/client';
import { QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { appRoutes } from './App';
import './index.css';
import { ErrorBoundary, ErrorFallback } from './components/error-boundary';
import { queryClient } from './lib/query-client';
import { Toaster } from 'sonner';
import React from 'react';

// Keep a stable router instance for the app lifetime.
const router = createBrowserRouter(appRoutes);

async function checkAppVersion() {
  try {
    const response = await fetch('/version.json', { 
      cache: 'no-store',
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const contentType = response.headers.get('content-type');
    if (!contentType || !contentType.includes('application/json')) {
      console.warn('version.json returned non-JSON content, skipping version check');
      return;
    }
    
    const { version: latestVersion } = await response.json();

    const currentVersion = localStorage.getItem('appVersion');

    if (currentVersion && currentVersion !== latestVersion) {
      console.log('🟡 New version detected. Forcing reload...');
      localStorage.setItem('appVersion', latestVersion);
      window.location.replace(window.location.href); // Force reload
    } else {
      localStorage.setItem('appVersion', latestVersion);
    }
  } catch (err) {
    console.warn('Failed to check version.json:', err);
  }
}

// Wrap the router creation in QueryClientProvider context
const AppWithProviders = () => (
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ErrorBoundary fallback={({ resetError }) => <ErrorFallback resetError={resetError} />}>
        <RouterProvider router={router} />
      </ErrorBoundary>
      <Toaster position="bottom-center" richColors closeButton />
      {import.meta.env.DEV && import.meta.env.VITE_REACT_DEV_TOOLS === 'true' && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  </React.StrictMode>
);

checkAppVersion().finally(() => {
  createRoot(document.getElementById('root')!).render(<AppWithProviders />);
});
